import React from 'react';
import { PaymentMethodType } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/MultiDepositTab/MultiDepositTab.state';
import { LogoApplePay } from 'src/domains/common/paymentMethods/paymentOptions/LogoApplePay';
import { LogoGooglePay } from 'src/domains/common/paymentMethods/paymentOptions/LogoGooglePay';
import { LogoYaspa } from 'src/domains/common/paymentMethods/paymentOptions/LogoYaspa';
import { VisaCard } from 'src/domains/common/paymentMethods/paymentCards/VisaCard';
import { MaestroCard } from 'src/domains/common/paymentMethods/paymentCards/MaestroCard';
import { YaspaCard } from 'src/domains/common/paymentMethods/paymentCards/YaspaCard';
import { ApplePayCard } from 'src/domains/common/paymentMethods/paymentCards/ApplePayCard';
import { GooglePayCard } from 'src/domains/common/paymentMethods/paymentCards/GooglePayCard';
import { useCommon, Common } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { isMobileVersionEqualOrHigher } from 'src/utils/mobileApp';
import { LogoDebitCardWrapper } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/PaymentMethodsConfig/paymentMethodsConfig.style';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';

export interface PaymentMethodConfigType {
    id: PaymentMethodType;
    title: string;
    description: string;
    logos?: boolean;
}

interface PaymentAvailabilityType {
    isApplePaySupported: boolean;
    isGooglePaySupported: boolean;
    shouldDisplayYaspa: boolean;
    isDebitCardSupported: boolean;
}

const usePaymentAvailability = (): PaymentAvailabilityType => {
    const common = useCommon();
    const configComponents = ConfigComponents.get(common);
    const environmentState = EnvironmentState.get(common);
    const basicDataModel = BasicDataModel.get(common);
    const paymentsProviders = configComponents.config.paymentProviders;
    const hideApplePayForEuro =
        basicDataModel.currency === 'EUR' && configComponents.config.applePayHiddenForEuro === true;
    const applePayEnabled =
        paymentsProviders.applePay !== null &&
        (paymentsProviders.applePay.type === 'worldpay' || paymentsProviders.applePay.applePayEnabled) &&
        hideApplePayForEuro === false;

    // Device-level availability is determined at runtime by the Google Pay SDK's isReadyToPay check
    const isGooglePaySupported = paymentsProviders.googlePay !== null;

    const isMobileApp = environmentState.isMobileApp();
    const mobileAppVersion = environmentState.mobileAppVersion;
    const yaspaMobileAppValidVersion = configComponents.config.yaspaMobileAppValidVersion;
    const shouldDisplayYaspaForMobileApp = isMobileApp
        ? isMobileVersionEqualOrHigher(mobileAppVersion, yaspaMobileAppValidVersion)
        : true;
    const isYaspaNotAllowed =
        basicDataModel.currency === 'EUR' &&
        paymentsProviders.openBanking !== null &&
        paymentsProviders.openBanking.isEuroAllowed === false;
    const shouldDisplayYaspa =
        paymentsProviders.openBanking !== null &&
        shouldDisplayYaspaForMobileApp &&
        isYaspaNotAllowed === false &&
        paymentsProviders.openBanking.allowOnlyWithdraw === false;
    const isDebitCardSupported = paymentsProviders.debitCard.type !== null;

    return {
        isApplePaySupported: applePayEnabled,
        isGooglePaySupported,
        shouldDisplayYaspa,
        isDebitCardSupported,
    };
};

export const usePaymentMethodsConfig = (): PaymentMethodConfigType[] => {
    const { isApplePaySupported, isGooglePaySupported, shouldDisplayYaspa, isDebitCardSupported } =
        usePaymentAvailability();

    const methods: PaymentMethodConfigType[] = [];

    if (isDebitCardSupported === true) {
        methods.push({
            id: 'debit-card',
            title: 'Debit Card',
            description: 'The most straightforward way to pay. Funds are deducted directly from your bank account.',
        });
    }

    if (isApplePaySupported === true) {
        methods.push({
            id: 'apple-pay',
            title: 'Apple Pay',
            description: 'Make fast, secure deposits with a tap or glance. Your card details are never shared.',
        });
    }

    if (isGooglePaySupported === true) {
        methods.push({
            id: 'google-pay',
            title: 'Google Pay',
            description: 'Make fast, secure deposits with a tap or fingerprint. Your card details are never shared.',
        });
    }

    if (shouldDisplayYaspa === true) {
        methods.push({
            id: 'yaspa',
            title: 'Yaspa',
            description: 'A quick and secure way to deposit directly from your bank account without using a card.',
            logos: true,
        });
    }

    return methods;
};

export const getPaymentMethodById = (
    id: PaymentMethodType,
    methods: PaymentMethodConfigType[]
): PaymentMethodConfigType | undefined => {
    return methods.find((method) => method.id === id);
};

export const getDefaultPaymentMethod = (common: Common): PaymentMethodType => {
    const configComponents = ConfigComponents.get(common);

    const isDebitCardSupported = configComponents.config.paymentProviders.debitCard.type !== null;
    if (isDebitCardSupported) {
        return 'debit-card';
    }

    const applePay = configComponents.config.paymentProviders.applePay;
    const applePayEnabled = applePay !== null && (applePay.type === 'worldpay' || applePay.applePayEnabled);
    if (applePayEnabled) {
        return 'apple-pay';
    }

    const isGooglePaySupported = configComponents.config.paymentProviders.googlePay !== null;
    if (isGooglePaySupported) {
        return 'google-pay';
    }

    return 'yaspa';
};

export const renderPaymentIcon = (method: PaymentMethodConfigType, isSignup: boolean): React.ReactNode => {
    switch (method.id) {
        case 'debit-card':
            return <LogoDebitCardWrapper variant={isSignup ? 'dark' : 'light'} />;
        case 'apple-pay':
            return <LogoApplePay variant={isSignup ? 'dark' : 'light'} />;
        case 'google-pay':
            return <LogoGooglePay />;
        case 'yaspa':
            return <LogoYaspa />;
        default:
            return null;
    }
};

export const usePaymentCardLogos = (): React.ReactNode => {
    const { isApplePaySupported, isGooglePaySupported, shouldDisplayYaspa } = usePaymentAvailability();
    return (
        <>
            <VisaCard />
            <MaestroCard />
            {shouldDisplayYaspa === true && <YaspaCard />}
            {isApplePaySupported === true && <ApplePayCard />}
            {isGooglePaySupported === true && <GooglePayCard />}
        </>
    );
};
